<section id="liquidity-section" class="max-w-[1320px] mx-auto px-6 lg:px-10 pb-16 pt-8 animate-fade-in relative z-10">

  <div class="mb-10 text-left">
    <h2 class="font-display font-black text-[32px] md:text-[40px] text-white tracking-wide">Liquidity Pools</h2>
    <p class="text-text-muted text-[14.5px] mt-2">Provide liquidity to earn trading fees and farming rewards.</p>
    
    <div class="mt-6 p-4 rounded-xl bg-gold/10 border border-gold/30 flex items-start gap-3">
      <svg class="w-5 h-5 text-gold flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <p class="text-white text-[13.5px] leading-relaxed">
        <span class="font-bold text-gold">Important:</span> To participate in DEX.finance liquidity pools, you will need wrapped tokens (like WETH, WBTC, or WBNB). You can easily wrap your native tokens using the 'Swap' feature in your wallet before connecting. Remember to always keep some native tokens (like ETH or BNB) in your wallet to cover network gas fees!
      </p>
    </div>
  </div>

  <div class="flex flex-col min-[1250px]:flex-row gap-6 w-full">
    <!-- LEFT COLUMN (Main Content) -->
    <div class="w-full min-[1250px]:w-[72%] flex flex-col gap-6">

      <!-- 4 KPI CARDS -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
        <!-- KPI 1 -->
        <div
          class="card p-4 rounded-xl bg-[#0c0c0c]/80 border border-white/[0.08] backdrop-blur-xl hover:border-white/[0.15] transition-colors">
          <div class="flex items-center gap-2 mb-3">
            <div class="text-gold">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <span class="text-[11px] font-bold text-text-muted">Total Value Locked</span>
          </div>
          <div class="text-[20px] font-black text-white mb-1">{{kpiData.tvl.value}}</div>
          <div class="text-[11px] font-bold" [ngClass]="{'text-[#20d489]': kpiData.tvl.change.startsWith('+'), 'text-red-500': kpiData.tvl.change.startsWith('-')}">
            {{kpiData.tvl.change}} <span class="text-text-dim font-normal">vs last 24h</span>
          </div>
        </div>
        <!-- KPI 2 -->
        <div
          class="card p-4 rounded-xl bg-[#0c0c0c]/80 border border-white/[0.08] backdrop-blur-xl hover:border-white/[0.15] transition-colors">
          <div class="flex items-center gap-2 mb-3">
            <div class="text-gold">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <span class="text-[11px] font-bold text-text-muted">Volume (24H)</span>
          </div>
          <div class="text-[20px] font-black text-white mb-1">{{kpiData.volume.value}}</div>
          <div class="text-[11px] font-bold" [ngClass]="{'text-[#20d489]': kpiData.volume.change.startsWith('+'), 'text-red-500': kpiData.volume.change.startsWith('-')}">
            {{kpiData.volume.change}} <span class="text-text-dim font-normal">vs last 24h</span>
          </div>
        </div>
        <!-- KPI 3 -->
        <div
          class="card p-4 rounded-xl bg-[#0c0c0c]/80 border border-white/[0.08] backdrop-blur-xl hover:border-white/[0.15] transition-colors">
          <div class="flex items-center gap-2 mb-3">
            <div class="text-gold">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <span class="text-[11px] font-bold text-text-muted">Fees (24H)</span>
          </div>
          <div class="text-[20px] font-black text-white mb-1">{{kpiData.fees.value}}</div>
          <div class="text-[11px] font-bold" [ngClass]="{'text-[#20d489]': kpiData.fees.change.startsWith('+'), 'text-red-500': kpiData.fees.change.startsWith('-')}">
            {{kpiData.fees.change}} <span class="text-text-dim font-normal">vs last 24h</span>
          </div>
        </div>
        <!-- KPI 4 -->
        <div
          class="card p-4 rounded-xl bg-[#0c0c0c]/80 border border-white/[0.08] backdrop-blur-xl hover:border-white/[0.15] transition-colors relative overflow-hidden">
          <div class="flex items-center gap-2 mb-3">
            <div class="text-gold">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <span class="text-[11px] font-bold text-text-muted">Avg. APR</span>
          </div>
          <div class="text-[20px] font-black text-white mb-1">{{kpiData.apr.value}}</div>
          <div class="text-[11px] font-bold" [ngClass]="{'text-[#20d489]': kpiData.apr.change.startsWith('+'), 'text-red-500': kpiData.apr.change.startsWith('-')}">
            {{kpiData.apr.change}} <span class="text-text-dim font-normal">vs last 24h</span>
          </div>
        </div>
      </div>

      <!-- FILTER BAR -->
      <div class="flex flex-col md:flex-row items-center justify-between gap-4 mt-2">
        <div class="flex items-center gap-3 w-full md:w-auto flex-1">
          <div class="relative w-full md:w-64">
            <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-dim" fill="none" viewBox="0 0 24 24"
              stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input type="text" [(ngModel)]="searchQuery" (ngModelChange)="onSearchChange()"
              placeholder="Search by token or pool"
              class="w-full bg-[#0c0c0c]/80 border border-white/[0.08] rounded-[10px] py-2 pl-9 pr-4 text-[13px] font-bold text-white focus:border-gold/30 outline-none transition-colors">
          </div>
          <div class="relative hidden sm:block w-36">
            <select [(ngModel)]="activeFeeFilter" (ngModelChange)="setFeeFilter($event)"
              class="w-full bg-[#0c0c0c]/80 border border-white/[0.08] rounded-[10px] py-2 px-3 text-[13px] font-bold text-white outline-none appearance-none cursor-pointer">
              @for (fee of feeTiers; track fee) {
              <option [value]="fee">{{fee === 'All' ? 'All Fee Tiers' : fee}}</option>
              }
            </select>
            <svg class="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 text-text-dim pointer-events-none" fill="none"
              viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <div class="relative hidden sm:block w-48">
            <select [(ngModel)]="activeSort" (ngModelChange)="onSortChange()"
              class="w-full bg-[#0c0c0c]/80 border border-white/[0.08] rounded-[10px] py-2 px-3 text-[13px] font-bold text-white outline-none appearance-none cursor-pointer">
              <option value="Sort by: TVL (High to Low)">Sort by: TVL (High to Low)</option>
              <option value="Sort by: APR (High to Low)">Sort by: APR (High to Low)</option>
              <option value="Sort by: Volume">Sort by: Volume</option>
            </select>
            <svg class="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 text-text-dim pointer-events-none" fill="none"
              viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        <button (click)="createNewPosition()"
          class="btn-gold py-2.5 px-5 rounded-[10px] text-[13px] font-extrabold tracking-wide w-full md:w-auto shadow-[0_0_15px_rgba(245,183,59,0.15)] flex items-center gap-1.5 justify-center">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          New Position
        </button>
      </div>

      <!-- POOLS TABLE -->
      <div class="card rounded-[20px] bg-[#0c0c0c]/90 border border-white/[0.08] backdrop-blur-xl shadow-xl overflow-hidden">
        <div class="overflow-x-auto w-full">
          <table class="w-full text-left border-collapse min-w-[800px]">
            <thead class="border-b border-white/[0.05]">
              <tr class="text-text-muted text-[11px] font-bold uppercase tracking-wider">
                <th class="py-4 px-4 w-12 text-center">#</th>
                <th class="py-4 px-4">Pool</th>
                <th class="py-4 px-4">Protocol</th>
                <th class="py-4 px-4">Fee tier</th>
                <th class="py-4 px-4 text-right">TVL</th>
                <th class="py-4 px-4 text-right">Pool APR</th>
                <th class="py-4 px-4 text-right">1D vol</th>
                <th class="py-4 px-4 text-right">1D vol/TVL</th>
              </tr>
            </thead>
            <tbody class="text-sm">
              <tr *ngFor="let pool of pools; let i = index" [routerLink]="['/liquidity', pool.address]" class="border-b border-white/[0.05] hover:bg-white/[0.04] cursor-pointer transition-colors">
                <td class="py-4 px-4 text-center text-text-muted font-bold text-[12px] whitespace-nowrap">{{ getIndex(i) }}</td>
                <td class="py-4 px-4 whitespace-nowrap">
                  <div class="flex items-center gap-2 pl-2">
                    <div class="flex -space-x-2">
                      <img *ngIf="pool.token0.logoUrl" [src]="pool.token0.logoUrl" class="w-6 h-6 rounded-full object-cover border border-[#050505] bg-[#111]" />
                      <div *ngIf="!pool.token0.logoUrl" class="w-6 h-6 rounded-full flex items-center justify-center font-black text-white text-[8px] relative overflow-hidden bg-white/10 border border-[#050505] z-10">
                        {{pool.token0.symbol.substring(0, 1)}}
                      </div>
                      
                      <img *ngIf="pool.token1.logoUrl" [src]="pool.token1.logoUrl" class="w-6 h-6 rounded-full object-cover border border-[#050505] bg-[#111]" />
                      <div *ngIf="!pool.token1.logoUrl" class="w-6 h-6 rounded-full flex items-center justify-center font-black text-white text-[8px] relative overflow-hidden bg-white/10 border border-[#050505]">
                        {{pool.token1.symbol.substring(0, 1)}}
                      </div>
                    </div>
                    <span class="text-white font-black text-[13.5px]">{{pool.token0.symbol}}/{{pool.token1.symbol}}</span>
                  </div>
                </td>
                <td class="py-4 px-4 text-text-muted font-bold text-[12px] whitespace-nowrap">v3</td>
                <td class="py-4 px-4 text-white font-bold text-[12px] whitespace-nowrap">{{ (pool.feeTier / 10000) | number:'1.0-4' }}%</td>
                <td class="py-4 px-4 text-right font-black text-white text-[13px] whitespace-nowrap">${{ formatNumber(pool.tvlUsd || 0) }}</td>
                <td class="py-4 px-4 text-right font-bold text-[12.5px] text-emerald-400 whitespace-nowrap">{{ (pool.apr || 0) | number:'1.2-2' }}%</td>
                <td class="py-4 px-4 text-right font-bold text-[12.5px] text-white whitespace-nowrap">${{ formatNumber(pool.volume24h || 0) }}</td>
                <td class="py-4 px-4 text-right font-bold text-[12.5px] text-text-muted whitespace-nowrap">{{ pool.tvlUsd > 0 ? (pool.volume24h / pool.tvlUsd | number:'1.2-2') : '0.00' }}</td>
              </tr>
              <tr *ngIf="pools.length === 0 && !loadingPools">
                <td colspan="8" class="py-12 text-center text-text-muted text-[13px]">
                  No pools found.
                </td>
              </tr>
              <tr *ngIf="pools.length === 0 && loadingPools">
                <td colspan="8" class="py-12 text-center text-text-muted text-[13px]">
                  Loading pools...
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="flex flex-col sm:flex-row justify-between items-center border-t border-white/[0.05] py-4 px-6 gap-4">
          <div class="text-text-muted text-[13px]">
            Showing {{pools.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}} to {{ (currentPage - 1) * itemsPerPage + pools.length }} of {{totalItems}}
          </div>
          <div class="flex items-center gap-3">
            <button (click)="changePage(-1)" class="bg-[#050505] hover:bg-gold/10 border border-white/10 hover:border-gold/30 text-white hover:text-gold transition-all duration-300 font-bold text-[13px] px-4 py-2 rounded-xl flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#050505] disabled:hover:border-white/10 disabled:hover:text-white"
              [disabled]="currentPage === 1">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" /></svg>
              Prev
            </button>
            <div class="text-white text-[13px] font-bold">
              Page {{currentPage}} of {{totalPages}}
            </div>
            <button (click)="changePage(1)" class="bg-[#050505] hover:bg-gold/10 border border-white/10 hover:border-gold/30 text-white hover:text-gold transition-all duration-300 font-bold text-[13px] px-4 py-2 rounded-xl flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#050505] disabled:hover:border-white/10 disabled:hover:text-white"
              [disabled]="currentPage === totalPages">
              Next
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>
        </div>
      </div>

    </div>

    <!-- RIGHT COLUMN (Your Positions) -->
    <div class="w-full min-[1250px]:w-[28%]">
      <div
        class="card p-6 rounded-[20px] bg-[#0c0c0c]/90 border border-white/[0.08] backdrop-blur-xl h-full flex flex-col shadow-xl">
        <div class="flex items-center justify-between mb-8">
          <h3 class="font-display font-black text-[18px] text-white tracking-wide">Your Positions</h3>
          <a routerLink="/portfolio" class="text-[12px] font-bold text-gold hover:underline flex items-center gap-1">View all <svg
              class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg></a>
        </div>

        <div class="flex items-center justify-between mb-5">
          <div>
            <div class="text-[11px] font-bold text-text-muted uppercase tracking-wider mb-1">Total Liquidity</div>
            <div *ngIf="yourPositions.isLoadingPositions" class="h-7 bg-white/10 rounded w-24 animate-pulse"></div>
            <div *ngIf="!yourPositions.isLoadingPositions" class="text-[22px] font-black text-white">{{yourPositions.totalLiquidity}}</div>
          </div>
          <div class="text-right flex flex-col items-end">
            <div class="text-[11px] font-bold text-text-muted uppercase tracking-wider mb-1">Unclaimed Fees</div>
            <div *ngIf="yourPositions.isLoadingUnclaimedFees" class="h-7 bg-white/10 rounded w-24 animate-pulse"></div>
            <div *ngIf="!yourPositions.isLoadingUnclaimedFees" class="text-[22px] font-black text-white">{{yourPositions.unclaimedFees}}</div>
          </div>
        </div>

        <div class="flex items-center justify-between mb-8 pb-5 border-b border-white/[0.05]">
          <div class="text-[12px] text-text-muted font-bold uppercase tracking-wider">Avg. APR</div>
          <div class="text-[14px] font-black text-[#20d489]">{{yourPositions.avgApr}}</div>
        </div>

        <!-- Position List -->
        <div class="flex flex-col flex-1">
          @if (state.isWalletConnected()) {
            @if (yourPositions.isLoadingPositions) {
              <div class="flex flex-col items-center justify-center py-10 gap-3">
                <svg class="animate-spin h-6 w-6 text-gold" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p class="text-text-muted text-[13px] font-bold animate-pulse">Loading positions...</p>
              </div>
            } @else if (yourPositions.list.length === 0) {
              <div class="text-center py-8">
                <p class="text-text-muted text-[13px] font-bold">No positions found</p>
              </div>
            } @else {
              @for (pos of yourPositions.list; track pos.pair) {
              <div class="group cursor-pointer mb-5 last:mb-0">
                <!-- Header: Tokens and In Range -->
                <div class="flex items-center justify-between mb-2.5">
                  <div class="flex items-center gap-3">
                    <div class="flex -space-x-1.5">
                      <div class="w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-bold text-white z-10 border-2 border-[#0c0c0c] shadow-sm overflow-hidden" [style.backgroundColor]="pos.color1">
                         <img *ngIf="pos.image1" [src]="pos.image1" class="w-full h-full object-cover">
                         <span *ngIf="!pos.image1">{{pos.icon1?.charAt(0)}}</span>
                      </div>
                      <div class="w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-bold text-white border-2 border-[#0c0c0c] shadow-sm overflow-hidden" [style.backgroundColor]="pos.color2">
                         <img *ngIf="pos.image2" [src]="pos.image2" class="w-full h-full object-cover">
                         <span *ngIf="!pos.image2">{{pos.icon2?.charAt(0)}}</span>
                      </div>
                    </div>
                    <div>
                      <div class="flex items-center gap-1.5 mb-1.5">
                        <div class="text-[15px] font-bold text-white leading-none">{{pos.title}}</div>
                        <span class="px-1.5 py-0.5 rounded text-[10px] font-bold bg-white/10 text-white/70 tracking-wide mt-0.5">v3</span>
                        <span class="px-1.5 py-0.5 rounded text-[10px] font-bold bg-white/10 text-white/70 tracking-wide mt-0.5">{{pos.fee}}</span>
                      </div>
                      <div *ngIf="pos.inRange" class="flex items-center gap-1.5 text-[11px] font-bold mt-1 cursor-pointer hover:opacity-80 transition-opacity text-emerald-500" (click)="$event.stopPropagation(); openAddLiquidityWithParams(pos)">
                        <div class="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                        <span class="hover:underline">In range</span>
                        <span class="text-white/20 ml-1 no-underline">•</span>
                        <span class="text-white/40 ml-1 font-normal no-underline">ID: #{{ pos.tokenId || pos._id?.substring(pos._id.length - 6) || '---' }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Chart (Moved down for narrow cards) -->
                <div class="flex justify-end mb-3">
                   <app-sparkline-chart 
                      [data]="pos.chartData || []" 
                      [inRange]="pos.inRange">
                   </app-sparkline-chart>
                </div>

                <!-- Token Amounts -->
                <div class="flex items-center justify-between mt-3 bg-white/5 rounded-lg p-2.5 border border-white/5">
                  <div class="flex flex-col">
                    <div class="text-[10px] font-bold text-text-muted uppercase tracking-wide mb-0.5">{{pos.metric1Label}}</div>
                    <div class="text-[12.5px] text-white block tracking-normal font-bold">{{pos.metric1Value}}</div>
                  </div>
                  <div class="flex flex-col text-right">
                    <div class="text-[10px] font-bold text-text-muted uppercase tracking-wide mb-0.5">{{pos.metric2Label}}</div>
                    <div class="text-[12.5px] text-white block tracking-normal font-bold">{{pos.metric2Value}}</div>
                  </div>
                </div>

                <!-- Price Range -->
                <div *ngIf="pos.minPrice !== '0.00' || pos.maxPrice !== '0.00' || pos.isFullRange" class="flex items-center justify-between mt-2 bg-white/5 rounded-lg p-2.5 border border-white/5">
                  <ng-container *ngIf="!pos.isFullRange; else fullRangeTemplate">
                    <div class="flex flex-col">
                      <div class="text-[10px] font-bold text-text-muted uppercase tracking-wide mb-0.5">Min Price</div>
                      <div class="text-[12.5px] text-white block tracking-normal font-bold">{{pos.minPrice}} <span class="text-text-dim text-[10px] ml-0.5">{{pos.metric2Label}} per {{pos.metric1Label}}</span></div>
                    </div>
                    <div class="flex flex-col text-right">
                      <div class="text-[10px] font-bold text-text-muted uppercase tracking-wide mb-0.5">Max Price</div>
                      <div class="text-[12.5px] text-white block tracking-normal font-bold">{{pos.maxPrice}} <span class="text-text-dim text-[10px] ml-0.5">{{pos.metric2Label}} per {{pos.metric1Label}}</span></div>
                    </div>
                  </ng-container>
                  <ng-template #fullRangeTemplate>
                    <div class="flex flex-col w-full text-center py-1">
                      <div class="text-[10px] font-bold text-text-muted uppercase tracking-wide mb-0.5">Price Range</div>
                      <div class="text-[12.5px] text-emerald-400 block tracking-normal font-bold">Full Range</div>
                    </div>
                  </ng-template>
                </div>

                <!-- Buttons -->
                <div class="mt-3 flex justify-end">
                  <button (click)="$event.stopPropagation(); openClaimModal(pos)" class="bg-gold text-[#050505] border border-gold/30 hover:bg-gold/90 text-[11px] font-black px-4 py-1.5 rounded-[8px] transition-all shadow-[0_0_15px_rgba(245,183,59,0.15)]">Claim Fees</button>
                </div>

                <div class="h-px w-full bg-border-primary mt-5 group-last:hidden"></div>
              </div>
              }
            }
          }
        </div>

        <div class="mt-8">
          @if (state.isWalletConnected()) {
          <button (click)="createNewPosition()"
            class="w-full py-3.5 rounded-xl border border-gold/30 hover:border-gold hover:bg-gold/5 text-gold font-extrabold uppercase tracking-widest text-[13px] transition-all shadow-sm">New
            Position</button>
          } @else {
          <button (click)="state.openConnectModal()"
            class="w-full py-3.5 rounded-xl border border-gold/30 hover:border-gold hover:bg-gold/5 text-gold font-extrabold uppercase tracking-widest text-[13px] transition-all shadow-sm">Connect
            Wallet</button>
          }
        </div>

      </div>
    </div>
  </div>

</section>

<!-- Claim Fees Modal -->
<div *ngIf="isClaimModalOpen" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
  <!-- Backdrop -->
  <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" (click)="closeClaimModal()"></div>
  
  <!-- Modal Content -->
  <div class="bg-[#0c0c0c] border border-white/10 rounded-[20px] w-full max-w-[400px] relative z-10 shadow-2xl overflow-hidden animate-slide-up">
    <!-- Header -->
    <div class="px-6 py-5 border-b border-white/5 flex items-center justify-between">
      <h3 class="text-white font-bold text-[16px]">Claim Uncollected Fees</h3>
      <button (click)="closeClaimModal()" class="text-text-muted hover:text-white transition-colors">
        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
      </button>
    </div>

    <!-- Body -->
    <div class="p-6">
      
      <div class="bg-[#050505] border border-gold/20 rounded-[12px] p-6 mb-6 text-center relative overflow-hidden">
        <div class="absolute inset-0 bg-gradient-to-b from-gold/5 to-transparent"></div>
        <div class="relative z-10">
          <div class="text-white font-bold text-[13px] text-center mb-1">Total Fees Earned</div>
          <div class="text-gold font-black text-[22px] text-center drop-shadow-[0_0_15px_rgba(245,183,59,0.3)]">
            <ng-container *ngIf="isLoadingLiveFees">
              <div class="flex items-center justify-center pt-1 pb-1">
                <svg class="animate-spin h-6 w-6 text-gold" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
              </div>
            </ng-container>
            <ng-container *ngIf="!isLoadingLiveFees">
              <span>{{liveFeeAmount0}} {{selectedPosition?.token0Symbol || ''}}</span>
              <br/>
              <span *ngIf="selectedPosition?.token1Symbol">+ {{liveFeeAmount1}} {{selectedPosition?.token1Symbol}}</span>
            </ng-container>
          </div>
        </div>
      </div>

      <div class="mb-6">
        <div class="text-white font-bold text-[13px] mb-3 border-b border-white/5 pb-2">Fee Breakdown</div>
        <div class="flex flex-col gap-3">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <span class="text-white font-bold text-[12.5px]">{{selectedPosition?.token0Symbol}}</span>
            </div>
            <div class="text-right">
              <div *ngIf="isLoadingLiveFees" class="flex items-center justify-end">
                <svg class="animate-spin h-3.5 w-3.5 text-gold" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
              </div>
              <div *ngIf="!isLoadingLiveFees" class="text-white font-black text-[12.5px]">{{liveFeeAmount0}}</div>
              <div class="text-text-muted text-[10px] font-bold">
                <span *ngIf="isLoadingLiveFees">~$--</span>
                <span *ngIf="!isLoadingLiveFees">~${{ liveFeeUsdFormatted0 }}</span>
              </div>
            </div>
          </div>
          <div *ngIf="selectedPosition?.token1Symbol" class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <span class="text-white font-bold text-[12.5px]">{{selectedPosition?.token1Symbol}}</span>
            </div>
            <div class="text-right">
              <div *ngIf="isLoadingLiveFees" class="flex items-center justify-end">
                <svg class="animate-spin h-3.5 w-3.5 text-gold" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
              </div>
              <div *ngIf="!isLoadingLiveFees" class="text-white font-black text-[12.5px]">{{liveFeeAmount1}}</div>
              <div class="text-text-muted text-[10px] font-bold">
                <span *ngIf="isLoadingLiveFees">~$--</span>
                <span *ngIf="!isLoadingLiveFees">~${{ liveFeeUsdFormatted1 }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <button (click)="confirmClaimFees()" [disabled]="isClaimingFees || hasNoFees || isLoadingLiveFees" class="w-full bg-gold hover:bg-gold/90 text-[#050505] font-black text-[14px] py-3.5 rounded-[12px] transition-all shadow-[0_0_20px_rgba(245,183,59,0.2)] disabled:opacity-50 disabled:cursor-not-allowed">
        <span *ngIf="!isClaimingFees">{{ hasNoFees ? 'No Fees to Collect' : 'Collect Fees' }}</span>
        <span *ngIf="isClaimingFees" class="flex items-center justify-center gap-2">
          <svg class="animate-spin h-4 w-4 text-[#050505]" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
          Collecting...
        </span>
      </button>
    </div>
  </div>
</div>